import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUserFriends, FaClipboardCheck, FaExclamationTriangle } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StatsCard from '../components/StatsCard';
import ChartComponent from '../components/ChartComponent';
import Calendar from '../components/Calendar';
import RecentReservations from '../components/RecentReservations';
import ChildrenProfiles from '../components/ChildrenProfiles';
import { monthlyStats, weekdayStats, reservations, children, dailyCapacity } from '../data/mockData';

const Dashboard: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(true);
  
  // Simuler un chargement initial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Calculer des statistiques de base
  const totalChildren = children.length;
  const pendingReservations = reservations.filter(r => r.status === 'pending').length;
  const todayCapacity = dailyCapacity.find(d => {
    const today = new Date().toISOString().split('T')[0];
    return d.date === today;
  }) || { maxCapacity: 5, currentOccupancy: 0 };
  const capacityPercentage = Math.round((todayCapacity.currentOccupancy / todayCapacity.maxCapacity) * 100);

  // Données pour les graphiques
  const monthlyData = {
    labels: monthlyStats.map(stat => stat.month),
    datasets: [
      {
        label: 'Nombre d\'inscriptions',
        data: monthlyStats.map(stat => stat.inscriptions),
        backgroundColor: 'rgba(152, 255, 152, 0.6)',
        borderColor: 'rgba(152, 255, 152, 1)',
        borderWidth: 2,
      },
    ],
  };

  const weekdayData = {
    labels: weekdayStats.map(stat => stat.jour),
    datasets: [
      {
        label: 'Taux d\'occupation (%)',
        data: weekdayStats.map(stat => stat.taux),
        backgroundColor: 'rgba(173, 216, 230, 0.6)',
        borderColor: 'rgba(173, 216, 230, 1)',
        borderWidth: 2,
        tension: 0.4,
      },
    ],
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ 
            duration: 0.5,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
          className="flex flex-col items-center"
        >
          <img src="/logo192.png" alt="Logo" className="w-16 h-16 mb-3" />
          <h2 className="text-xl font-semibold text-gray-700">Home des Prés</h2>
          <p className="text-gray-500 mt-2">Chargement du tableau de bord...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      {/* Hero section avec fond décoratif */}
      <div className="pt-24 pb-12 px-6 bg-gradient-to-r from-[#98FF98] to-[#ADD8E6] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="pattern-circles" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse" patternContentUnits="userSpaceOnUse">
              <circle id="pattern-circle" cx="10" cy="10" r="8" fill="none" stroke="white" strokeWidth="0.5"></circle>
            </pattern>
            <rect x="0" y="0" width="100%" height="100%" fill="url(#pattern-circles)"></rect>
          </svg>
        </div>
        
        <div className="container mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-white text-center"
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Tableau de Bord</h1>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              Bienvenue sur l'interface de gestion du service d'accueil temporaire pour enfants du Home des Prés.
            </p>
          </motion.div>
        </div>
      </div>
      
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8">
          {/* Statistiques */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <motion.div variants={itemVariants}>
              <StatsCard
                title="Enfants inscrits"
                value={totalChildren}
                icon={<FaUserFriends className="text-blue-500" size={20} />}
                color="bg-blue-100 bg-opacity-70"
                percentage={12}
                isIncreasing={true}
              />
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <StatsCard
                title="Capacité d'aujourd'hui"
                value={`${todayCapacity.currentOccupancy}/${todayCapacity.maxCapacity}`}
                icon={<FaCalendarAlt className="text-green-500" size={20} />}
                color="bg-green-100 bg-opacity-70"
                percentage={capacityPercentage}
                isIncreasing={false}
              />
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <StatsCard
                title="Demandes en attente"
                value={pendingReservations}
                icon={<FaClipboardCheck className="text-yellow-500" size={20} />}
                color="bg-yellow-100 bg-opacity-70"
                percentage={8}
                isIncreasing={true}
              />
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <StatsCard
                title="Jours bloqués"
                value={4}
                icon={<FaExclamationTriangle className="text-red-500" size={20} />}
                color="bg-red-100 bg-opacity-70"
                percentage={0}
                isIncreasing={false}
              />
            </motion.div>
          </motion.div>
          
          {/* Graphiques */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
          >
            <motion.div variants={itemVariants}>
              <ChartComponent
                type="bar"
                title="Inscriptions Mensuelles"
                data={monthlyData}
              />
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <ChartComponent
                type="line"
                title="Occupation par Jour de la Semaine (%)"
                data={weekdayData}
              />
            </motion.div>
          </motion.div>
          
          {/* Calendrier et réservations récentes */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
          >
            <motion.div variants={itemVariants}>
              <Calendar onDateSelect={setSelectedDate} />
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <RecentReservations />
            </motion.div>
          </motion.div>
          
          {/* Profils des enfants */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <ChildrenProfiles />
            </motion.div>
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Dashboard;
